type JsonObj = Record<string, any>;

/**
 * Parse HTML/XML string into a simple JSON object.
 * - Attributes become object fields.
 * - If an element has only text -> put it into `text`.
 * - Child elements become fields; repeated tags become arrays.
 */
export const parseHtmlToJson = (str: string): JsonObj => {
    const doc = new DOMParser().parseFromString(`<root>${str}</root>`, "application/xml");
    const root = doc.documentElement;

    function nodeToObj(node: Node): string | null | JsonObj {
        // Text node -> trimmed string (or null if empty)
        if (node.nodeType === Node.TEXT_NODE) {
            const t = (node.nodeValue ?? "").trim();
            return t ? t : null;
        }
        // Skip non-element nodes (comments, etc.)
        if (node.nodeType !== Node.ELEMENT_NODE) return null;

        const el = node as Element;
        const obj: JsonObj = {};

        // Copy attributes
        for (let i = 0; i < el.attributes.length; i++) {
            const a = el.attributes[i];
            obj[a.name] = a.value;
        }

        // Process children
        const kids: (string | JsonObj)[] = [];
        for (let i = 0; i < el.childNodes.length; i++) {
            const v = nodeToObj(el.childNodes[i]);
            if (v != null) kids.push(v);
        }

        // If single text child -> store as { text: "..." }
        if (kids.length === 1 && typeof kids[0] === "string") {
            obj.text = kids[0];
        } else {
            // Merge element children by tag name; group repeats into arrays
            for (const k of kids) {
                if (typeof k === "object") {
                    const [tag, val] = Object.entries(k)[0];
                    if (obj[tag] === undefined) obj[tag] = val;
                    else if (Array.isArray(obj[tag])) obj[tag].push(val);
                    else obj[tag] = [obj[tag], val];
                }
            }
        }

        return { [el.tagName]: obj };
    }

    // Unwrap <root> so top-level tags become fields
    const wrapped = nodeToObj(root) as JsonObj;
    return (wrapped.root ?? wrapped) as JsonObj;
}
